import { useFavorites } from '../../state-management/server-state/use-favorites';
import RowHeading from '../row-heading';
import { useUserWorkoutDetails } from '../../state-management/server-state/use-user-workout-details';
import { isRestrictedUserType } from '../../util/is-restricted-user-type';
import { useUserSeriesDetails } from '../../state-management/server-state/use-user-series-details';
import { useUserType } from '../../state-management/selectors/use-user-type';
import { Text } from './text';
import { useMatrixCommercial } from '../../hooks/use-matrix-commercial';
import CardSeries from '../cards/series';
import CardWorkout from '../cards/workout';
import { intensityConverter } from '../../util/intensity';
import { GlobalText } from '../../i18n/global-text';
import { getUserNavigationHandler, ROWTITLES } from '../../util/navigation-utils';
import './index.scss';

export default function FavoritesRow(): JSX.Element {
  const userType = useUserType();
  const isRestrictedUser = isRestrictedUserType(userType);
  const isMatrixCommerical = useMatrixCommercial();

  const favoritesQuery = useFavorites(isRestrictedUser);
  const { data } = favoritesQuery;

  const workoutIds = data?.filter((favorite) => favorite.favoriteType === 'workout').map((favorite) => favorite.id);
  const userWorkoutDetailsQuery = useUserWorkoutDetails(workoutIds);

  const seriesIds = data?.filter((favorite) => favorite.favoriteType === 'series').map((favorite) => favorite.id);
  const userSeriesDetailsQuery = useUserSeriesDetails(seriesIds);

  const zippedData = data?.map((favorite) => {
    switch (favorite.favoriteType) {
      case 'workout':
        return {
          favorite,
          favoriteType: favorite.favoriteType,
          userWorkoutDetail: userWorkoutDetailsQuery.data?.find((detail) => detail.workoutId === favorite.id),
        };
      case 'series':
        return {
          favorite,
          favoriteType: favorite.favoriteType,
          userSeriesDetail: userSeriesDetailsQuery.data?.find((detail) => detail.seriesId === favorite.id),
        };
    }
  });
  const isFetching =
    favoritesQuery.isFetching || userWorkoutDetailsQuery.isFetching || userSeriesDetailsQuery.isFetching;
  const noValidData = !zippedData || zippedData.length === 0;
  const hideRow = isRestrictedUser || noValidData || isMatrixCommerical;

  if (hideRow) {
    return null;
  }

  return (
    <div className="row-favorites">
      <RowHeading title={Text.title} fetching={isFetching} link="https://www.ifit.com/library?page=favorites" />
      <div className="cards" style={{ ['--cards-per-row' as any]: zippedData.length }}>
        {zippedData.map((item) => {
          const clickHandler = getUserNavigationHandler(item.favorite.url, ROWTITLES.FAVORITES);

          if (item.favoriteType === 'workout') {
            const { favorite, userWorkoutDetail } = item;
            const { id, imageUrl, intensity, averageRating, title } = favorite;
            const formatDate = userWorkoutDetail?.completedDate
              ? new Date(userWorkoutDetail.completedDate).toLocaleDateString()
              : null;
            const showCompleted = formatDate ? GlobalText.completedDate(formatDate) : null;
            const estimates = {
              calories: userWorkoutDetail?.estimates.calories,
              distance: userWorkoutDetail?.estimates.distance,
              time: userWorkoutDetail?.estimates.duration,
            };
            const formattedRating = averageRating ? `${Number.parseFloat(`${averageRating}`).toFixed(1)}` : null;

            return (
              <CardWorkout
                key={id}
                onClick={clickHandler}
                completed={showCompleted}
                estimates={estimates}
                imageUrl={imageUrl}
                tagPrimary={intensityConverter(intensity)}
                averageRating={formattedRating}
                title={title}
                locked={userWorkoutDetail?.locked}
              />
            );
          } else {
            const { userSeriesDetail, favorite } = item;
            const { imageUrl, intensity, id, averageRating, title, workoutsCount } = favorite;
            const formattedRating = averageRating ? `${Number.parseFloat(`${averageRating}`).toFixed(1)}` : null;

            return (
              <CardSeries
                key={id}
                onClick={clickHandler}
                imageUrl={imageUrl}
                leadLine={intensityConverter(intensity)}
                title={title}
                averageRating={formattedRating}
                tagPrimary={GlobalText.numberOfWorkouts(workoutsCount)}
                locked={userSeriesDetail?.locked}
                completed={userSeriesDetail?.completedDate}
              />
            );
          }
        })}
      </div>
    </div>
  );
}
